/**
 * SlotPlaceholder Component
 * =========================
 * Displays an interactive placeholder in empty layout slots.
 * Shows slot information and allows users to add components.
 * 
 * Features:
 * - Displays slot name, icon, and description
 * - Shows suggested component types
 * - Click to trigger component selector
 * - Visual feedback on hover
 * - Tips and guidance for users
 * 
 * Used by: PreviewRenderer.js when a slot is empty
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.slot - Slot definition from layout
 * @param {string} props.layoutId - Current layout ID
 * @param {Function} props.onAddComponent - Callback when user wants to add component
 * @param {Object} props.slotDefinition - Detailed slot definition from slotDefinitions.js
 */

import React, { useState } from 'react';
import { 
  getSlotIcon, 
  getSlotTips, 
  getSlotPrimaryComponents 
} from '../../../config/slotDefinitions';

const SlotPlaceholder = ({ 
  slot, 
  layoutId, 
  onAddComponent,
  slotDefinition 
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Get slot information
  const slotIcon = slotDefinition?.icon || getSlotIcon(slot.id) || '📦';
  const slotName = slot.name || slotDefinition?.name || 'Content Area';
  const slotDescription = slot.placeholder || slotDefinition?.description || 'Click to add content';
  const slotTips = getSlotTips(slot.id);
  const primaryComponents = getSlotPrimaryComponents(slot.id);
  const suggestedComponents = slot.suggestedComponents || slotDefinition?.suggestedComponents || [];

  // Handle click - trigger component addition
  const handleClick = () => {
    if (onAddComponent) {
      // Pass slot information to parent for filtering component suggestions
      onAddComponent({
        slotId: slot.id,
        slotName: slotName,
        suggestedComponents: suggestedComponents,
        primaryComponents: primaryComponents
      });
    }
  };

  // Get component type labels
  const getComponentLabel = (type) => {
    const labels = {
      text: '📝 Text',
      paragraph: '📄 Paragraph',
      header: '📰 Header',
      button: '🔘 Button',
      image: '🖼️ Image',
      card: '🗂️ Card',
      list: '📋 List',
      form: '📋 Form',
      table: '📊 Table',
      carousel: '🎠 Carousel',
      video: '🎥 Video',
      breadcrumb: '🍞 Breadcrumb',
      icon: '⭐ Icon'
    };
    return labels[type] || `📦 ${type}`;
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        width: '100%',
        minHeight: '120px',
        padding: '24px',
        margin: '8px 0',
        border: isHovered ? '2px dashed #3B82F6' : '2px dashed #D1D5DB',
        borderRadius: '12px',
        backgroundColor: isHovered ? '#F0F9FF' : '#F9FAFB',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background pattern on hover */}
      {isHovered && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `radial-gradient(circle, #3B82F6 1px, transparent 1px)`,
            backgroundSize: '20px 20px',
            opacity: 0.05,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* Content wrapper */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        {/* Slot Icon */}
        <div style={{
          fontSize: '40px',
          marginBottom: '8px',
          animation: isHovered ? 'bounce 0.5s ease' : 'none'
        }}>
          {slotIcon}
        </div>

        {/* Slot Name */}
        <div style={{
          fontSize: '16px',
          fontWeight: '700',
          color: isHovered ? '#1E40AF' : '#374151',
          marginBottom: '4px'
        }}>
          {slotName}
        </div>

        {/* Slot Description */}
        <div style={{
          fontSize: '13px',
          color: '#6B7280',
          marginBottom: '12px',
          maxWidth: '300px'
        }}>
          {slotDescription}
        </div>

        {/* Add Buttons: Stack or Row */}
        <div style={{ display: 'flex', gap: '8px', width: '100%', maxWidth: '350px' }}>
          <button
            type="button"
            style={{
              flex: 1,
              padding: '10px 16px',
              backgroundColor: isHovered ? '#10B981' : '#E5E7EB',
              color: isHovered ? '#FFFFFF' : '#6B7280',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: isHovered ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px'
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onAddComponent) {
                onAddComponent({ slotId: slot.id, direction: 'column' });
              }
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#10B981';
              e.currentTarget.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#E5E7EB';
              e.currentTarget.style.color = '#6B7280';
            }}
          >
            <span style={{ fontSize: '20px' }}>↓</span>
            <span>Stack (Vertical)</span>
          </button>
          
          <button
            type="button"
            style={{
              flex: 1,
              padding: '10px 16px',
              backgroundColor: isHovered ? '#3B82F6' : '#E5E7EB',
              color: isHovered ? '#FFFFFF' : '#6B7280',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: isHovered ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px'
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onAddComponent) {
                onAddComponent({ slotId: slot.id, direction: 'row' });
              }
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#3B82F6';
              e.currentTarget.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#E5E7EB';
              e.currentTarget.style.color = '#6B7280';
            }}
          >
            <span style={{ fontSize: '20px' }}>→</span>
            <span>Row (Horizontal)</span>
          </button>
        </div>

        {/* Suggested Components */}
        {suggestedComponents.length > 0 && (
          <div style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid #E5E7EB'
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '600',
              color: '#9CA3AF',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Suggested Components
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              justifyContent: 'center',
              maxWidth: '350px'
            }}>
              {primaryComponents.slice(0, 4).map((type, index) => (
                <span
                  key={index}
                  style={{
                    fontSize: '11px',
                    padding: '4px 10px',
                    backgroundColor: isHovered ? '#DBEAFE' : '#FFFFFF',
                    border: `1px solid ${isHovered ? '#93C5FD' : '#E5E7EB'}`,
                    borderRadius: '6px',
                    color: '#374151',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {getComponentLabel(type)}
                </span>
              ))}
              {suggestedComponents.length > 4 && (
                <span
                  style={{
                    fontSize: '11px',
                    padding: '4px 10px',
                    backgroundColor: '#F3F4F6',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    color: '#6B7280',
                    fontWeight: '500'
                  }}
                >
                  +{suggestedComponents.length - 4} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Tips (shown on hover) */}
        {isHovered && slotTips && (
          <div style={{
            marginTop: '12px',
            fontSize: '11px',
            color: '#3B82F6',
            fontStyle: 'italic',
            maxWidth: '300px',
            lineHeight: '1.4',
            animation: 'fadeIn 0.3s ease'
          }}>
            💡 {slotTips}
          </div>
        )}
      </div>

      {/* Inline animations */}
      <style>
        {`
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default SlotPlaceholder;












